/**
 * Event host identity — the pure half of the detail drawer's Host picker
 * (HostRow in EventOverlay.tsx). Kept out of the component so the identity
 * rules can be tested without rendering:
 *
 *  • option VALUES are staff ids (`listStaff` member `id`), never names;
 *  • two members who share a name are two options, told apart in the label;
 *  • a legacy host label with no id is never matched to a member by name — it
 *    stays visibly unlinked until someone picks a member, which sets the id;
 *  • clearing is explicit: `{ staffId: null, staffName: "" }`.
 */

/** Dropdown value for "the current host is a legacy label with no staff link".
 *  It is not an id and is never sent: it only ever appears as the CURRENT
 *  value, so choosing it changes nothing. The NUL prefix cannot occur in a real
 *  staff id. */
export const UNLINKED_HOST_VALUE = "\u0000unlinked-host";

/** The fields of a staff member (useStaff's StaffMember) the picker reads. */
export interface HostStaffMember {
  id: string;
  name?: string | null;
  email?: string | null;
  status?: string | null;
}

export interface HostOption {
  value: string;
  label: string;
}

/** The PATCH body for a host change. Selecting sends the id alone — the events
 *  worker resolves the canonical `staffName` from the member. A set may carry
 *  the name too, so the row's denormalised label matches the link immediately
 *  (CC, 2026-09-22). Clearing sends both, because a null id alone would leave
 *  the old label standing. */
export type HostUpdateInput =
  | { staffId: string; staffName?: string }
  | { staffId: null; staffName: "" };

const displayName = (m: HostStaffMember): string => (m.name || m.email || "").trim();
const fold = (s: string): string => s.toLocaleLowerCase();

/** The dropdown's current value: the linked id; else the unlinked marker when
 *  a legacy label is showing; else "" (No host). */
export function hostSelectedValue(hostId: string | null | undefined, hostName: string): string {
  if (hostId) return hostId;
  return hostName ? UNLINKED_HOST_VALUE : "";
}

export function hostPickerOptions(input: {
  /** The event's canonical host id (UIEvent.hostId). */
  hostId: string | null | undefined;
  /** The event's host display label ("" when there is none). */
  hostName: string;
  members: HostStaffMember[];
  noHostLabel: string;
  /** Suffix that marks a legacy label as not linked to any staff member. */
  unlinkedHint: string;
}): HostOption[] {
  const { hostId, hostName, members, noHostLabel, unlinkedHint } = input;

  const people = members
    .filter((m) => m.id && (m.status ?? "active") === "active" && displayName(m))
    .map((m) => ({ id: m.id, label: displayName(m), email: (m.email ?? "").trim() }));

  // The linked host, kept when the active list does not carry it (a member
  // since deactivated, or a roster that has not loaded yet). Labelled with the
  // event's own resolved name first — that is what the server stored.
  if (hostId && !people.some((p) => p.id === hostId)) {
    const known = members.find((m) => m.id === hostId);
    people.push({
      id: hostId,
      label: hostName || (known ? displayName(known) : "") || hostId,
      email: (known?.email ?? "").trim(),
    });
  }

  const nameCount = new Map<string, number>();
  const emailCount = new Map<string, number>();
  for (const p of people) {
    nameCount.set(fold(p.label), (nameCount.get(fold(p.label)) ?? 0) + 1);
    if (p.email) emailCount.set(fold(p.email), (emailCount.get(fold(p.email)) ?? 0) + 1);
  }

  const options: HostOption[] = [{ value: "", label: noHostLabel }];
  for (const p of people) {
    const shared = (nameCount.get(fold(p.label)) ?? 0) > 1;
    if (!shared) { options.push({ value: p.id, label: p.label }); continue; }
    // Same name, different people: the email tells them apart; the id is the
    // fallback when there is no email, or the email is not unique either.
    const emailUsable = p.email && fold(p.email) !== fold(p.label) && emailCount.get(fold(p.email)) === 1;
    options.push({ value: p.id, label: `${p.label} · ${emailUsable ? p.email : p.id}` });
  }

  if (!hostId && hostName) options.push({ value: UNLINKED_HOST_VALUE, label: `${hostName} · ${unlinkedHint}` });
  return options;
}

/** `current` → `next` as a PATCH body, or null when nothing should be sent
 *  (no change, or the unlinked marker, which is never a real choice).
 *
 *  `staffId` is the canonical field and the ONLY one the server links on — a
 *  name sent without an id reads as "unlink the host" and clears
 *  `events.staff_id`, which is how five capture events ended up hosted by
 *  nobody's My Day. `staffName` rides along when the caller can supply it, so
 *  the denormalised label on the row matches the link rather than lagging it
 *  (CC, 2026-09-22); it is never sent alone. */
export function hostUpdateInput(current: string, next: string, nextName?: string): HostUpdateInput | null {
  if (next === current || next === UNLINKED_HOST_VALUE) return null;
  if (!next) return { staffId: null, staffName: "" };
  const name = (nextName || "").trim();
  return name ? { staffId: next, staffName: name } : { staffId: next };
}
